package er.prateek.crossly.ui.screens.math

import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.boundsInRoot
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import er.prateek.crossly.data.model.math.MathCellKind
import er.prateek.crossly.game.crossword.CellPos
import er.prateek.crossly.game.crossword.CellStatus
import er.prateek.crossly.game.math.MathCellState
import er.prateek.crossly.game.math.MathCrosswordEngine
import er.prateek.crossly.ui.screens.crossword.LetterDragState
import er.prateek.crossly.ui.theme.game

private val CellGap = 3.dp

/**
 * The math board. Cell size is solved from the space available (the largest square that fits both
 * the width and the height), reusing the crossword grid's responsive approach, so an irregular
 * grid renders without scrolling on a phone and scales up on desktop. BLOCK cells draw as empty
 * gaps, so the equations read as a crossing shape rather than a full rectangle.
 */
@Composable
fun MathGridView(
    engine: MathCrosswordEngine,
    modifier: Modifier = Modifier,
    minCellSize: Dp = 30.dp,
    maxCellSize: Dp = 76.dp,
    dragState: LetterDragState? = null,
) {
    BoxWithConstraints(modifier = modifier, contentAlignment = Alignment.Center) {
        val framePadding = 5.dp
        val horizontalSlack = CellGap * (engine.width - 1) + framePadding * 2
        val verticalSlack = CellGap * (engine.height - 1) + framePadding * 2
        val byWidth = (maxWidth - horizontalSlack) / engine.width
        val byHeight = if (constraints.hasBoundedHeight) {
            (maxHeight - verticalSlack) / engine.height
        } else {
            maxCellSize
        }
        val cellSize = minOf(byWidth, byHeight, maxCellSize).coerceAtLeast(minCellSize)

        Column(verticalArrangement = Arrangement.spacedBy(CellGap)) {
            for (row in 0 until engine.height) {
                Row(horizontalArrangement = Arrangement.spacedBy(CellGap)) {
                    for (col in 0 until engine.width) {
                        val cell = engine.cellAt(CellPos(row, col))
                        when {
                            cell == null || cell.kind == MathCellKind.BLOCK ->
                                Box(modifier = Modifier.size(cellSize))
                            cell.kind == MathCellKind.OPERATOR || cell.kind == MathCellKind.EQUALS ->
                                SymbolCell(text = cell.fixedText, size = cellSize)
                            else ->
                                NumberCell(
                                    cell = cell,
                                    size = cellSize,
                                    isSelected = engine.selected == cell.pos,
                                    isDropTarget = dragState?.hoveredCell == cell.pos,
                                    dragState = dragState,
                                    onClick = { engine.selectCell(cell.pos) },
                                )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun SymbolCell(text: String, size: Dp) {
    val colors = MaterialTheme.game
    Box(
        modifier = Modifier.size(size),
        contentAlignment = Alignment.Center,
    ) {
        Text(
            text = text,
            fontSize = (size.value * 0.46f).coerceIn(14f, 34f).sp,
            fontWeight = FontWeight.Bold,
            color = colors.inkMuted,
        )
    }
}

@Composable
private fun NumberCell(
    cell: MathCellState,
    size: Dp,
    isSelected: Boolean,
    isDropTarget: Boolean,
    dragState: LetterDragState?,
    onClick: () -> Unit,
) {
    val colors = MaterialTheme.game
    val targetFill = when {
        isDropTarget -> colors.accentSoft
        isSelected -> colors.cellFocus
        cell.status == CellStatus.GIVEN -> colors.cellGiven
        cell.status == CellStatus.CORRECT -> colors.cellCorrect
        cell.status == CellStatus.INCORRECT -> colors.cellIncorrect
        cell.status == CellStatus.REVEALED -> colors.cellRevealed
        else -> colors.cellFill
    }
    val fill by animateColorAsState(targetFill, tween(180), label = "mathCellFill")
    val borderColor = if (isSelected || isDropTarget) colors.accent else Color.Transparent
    val shape = RoundedCornerShape(size * 0.16f)

    val text = cell.userText
    val lengthFactor = when {
        text.length >= 3 -> 0.34f
        text.length == 2 -> 0.42f
        else -> 0.5f
    }

    if (dragState != null) {
        DisposableEffect(dragState, cell.pos) { onDispose { dragState.forgetCell(cell.pos) } }
    }

    Box(
        modifier = Modifier
            .size(size)
            // Only squares a digit may land on take part in drag-and-drop.
            .then(
                if (dragState != null && cell.editable) {
                    Modifier.onGloballyPositioned { dragState.registerCell(cell.pos, it.boundsInRoot()) }
                } else {
                    Modifier
                },
            )
            .clip(shape)
            .background(fill)
            .border(if (isSelected || isDropTarget) 2.5.dp else 1.dp, if (isSelected || isDropTarget) borderColor else colors.cellBorder, shape)
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                enabled = cell.editable,
                onClick = onClick,
            ),
        contentAlignment = Alignment.Center,
    ) {
        if (text.isNotEmpty()) {
            Text(
                text = text,
                fontSize = (size.value * lengthFactor).coerceIn(12f, 34f).sp,
                fontWeight = FontWeight.Bold,
                color = if (cell.given) colors.inkMuted else colors.cellText,
            )
        }
    }
}
